"use client";

import { useState, useCallback, useRef } from 'react';
import { useVadStore } from '@/shared/store/vad';
import { resolveVADState, shouldUpdateVADState, createVADDebugInfo } from './vad-state-resolver';
import type {
  VADInputs,
  VADState,
  VADControllerOptions,
  UseVADControllerReturn,
  VADStatus,
  VADStatusReason
} from '../types/vad-state';
import { DEFAULT_VAD_STATUS, VAD_BEHAVIOR_CONFIG, VAD_STATUS_DESCRIPTIONS } from '../const/vad-config';

/**
 * VAD 통합 컨트롤러 훅
 *
 * 책임:
 * - 여러 상태 입력을 종합하여 VAD 상태 결정
 * - 상태 변경 시 VAD 스토어 업데이트
 * - 상태 변경 추적 및 디버깅 지원
 */

export const useVADController = (
  options: VADControllerOptions = {}
): UseVADControllerReturn & { _updateVADState: (inputs: VADInputs) => VADState } => {
  const { onStatusChange, enableLogging = VAD_BEHAVIOR_CONFIG.ENABLE_LOGGING } = options;

  // VAD 스토어 액세스
  const setVadStatus = useVadStore(state => state.setVadStatus);

  // 내부 상태
  const [currentState, setCurrentState] = useState<VADState>({
    status: DEFAULT_VAD_STATUS,
    reason: 'consent_required',
  });

  // Ref로 현재 상태 추적 (stale closure 방지)
  const currentStateRef = useRef(currentState);
  currentStateRef.current = currentState;

  // 디버그 정보 저장용
  const debugInfoRef = useRef<ReturnType<typeof createVADDebugInfo> | null>(null);

  /**
   * VAD 상태 해결 및 업데이트
   */
  const resolveAndUpdateVADState = useCallback((inputs: VADInputs) => {
    const newState = resolveVADState(inputs);

    // currentStateRef.current로 최신 상태 참조 (stale closure 방지)
    if (shouldUpdateVADState(currentStateRef.current, newState)) {
      // 디버그 정보 생성
      const debugInfo = createVADDebugInfo(inputs, newState);
      debugInfoRef.current = debugInfo;

      // 로깅
      if (enableLogging) {
        console.log(`🎙️ [VAD] 상태 변경: ${currentStateRef.current.reason} → ${newState.reason}`, {
          status: `${currentStateRef.current.status} → ${newState.status}`,
          description: VAD_STATUS_DESCRIPTIONS[newState.reason],
          inputs,
        });
      }

      // 내부 상태 업데이트
      setCurrentState(newState);

      // VAD 스토어 업데이트
      setVadStatus(newState.status);

      // 외부 콜백 호출
      onStatusChange?.(newState);
    }

    return newState;
  }, [setVadStatus, onStatusChange, enableLogging]);

  /**
   * 공용 해결 함수 (외부에서 사용 가능)
   */
  const resolveVADStatePublic = useCallback((inputs: VADInputs): VADState => {
    return resolveVADState(inputs);
  }, []);

  /**
   * 디버그 정보 반환
   */
  const getStateDebugInfo = useCallback(() => {
    if (!debugInfoRef.current) {
      return {
        inputs: {
          isConsentVisible: false,
          currentChatMode: 'voice' as const,
          isMicOn: false,
          isLoading: false,
          isSpeaking: false,
          isAudioPlaying: false,
        },
        resolved: currentStateRef.current,
        timestamp: Date.now(),
      };
    }
    return debugInfoRef.current;
  }, []);

  return {
    // 현재 상태
    vadStatus: currentState.status,
    vadReason: currentState.reason,

    // 내부 사용 함수 (실제로는 resolveAndUpdateVADState를 사용하지만 인터페이스 일관성을 위해)
    resolveVADState: resolveVADStatePublic,

    // 디버깅
    getStateDebugInfo,

    // 내부 업데이트 함수 (custom-avatar-page에서 사용)
    _updateVADState: resolveAndUpdateVADState,
  };
};
